<template>
    <v-container fluid class="ai-history">

        <!-- Header -->
        <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px">
            <div>
                <h2 class="text-h6 font-weight-bold ai-history-ink">
                    <v-icon left color="deep-purple">mdi-robot-happy-outline</v-icon>
                    AI Assistant History
                </h2>
                <div v-if="source === 'app'" class="caption grey--text">What people asked the in-app AI Assistant Bubble, and the tools it used (kept since 23 Oct 2026)</div>
                <div v-else class="caption grey--text">Sessions from MCP connectors (claude.ai, ChatGPT, Cursor, n8n...) and the tools they called (kept since 27 Oct 2026)</div>
            </div>
            <v-spacer />
            <v-btn-toggle v-model="preset" dense mandatory color="deep-purple" @change="applyPreset">
                <v-btn small value="today">Today</v-btn>
                <v-btn small value="7">7 days</v-btn>
                <v-btn small value="30">30 days</v-btn>
                <v-btn small value="90">90 days</v-btn>
                <v-btn small value="custom">Custom</v-btn>
            </v-btn-toggle>
            <v-btn small outlined color="deep-purple" :loading="source === 'mcp' ? mcpLoading : (statsLoading || sessionsLoading || callsLoading)" @click="refresh">
                <v-icon small left>mdi-refresh</v-icon>Refresh
            </v-btn>
        </div>

        <v-tabs v-model="source" height="40" color="deep-purple" background-color="transparent" class="mb-3">
            <v-tab href="#app"><v-icon small left>mdi-robot-happy-outline</v-icon>AI Assistant</v-tab>
            <v-tab href="#mcp"><v-icon small left>mdi-connection</v-icon>MCP sessions</v-tab>
        </v-tabs>

        <mcp-sessions-panel
            v-if="source === 'mcp'"
            ref="mcp"
            :date-from.sync="dateFrom"
            :date-to.sync="dateTo"
            :custom="preset === 'custom'"
            @loading="mcpLoading = $event"
        />

        <template v-else>
        <!-- Filters -->
        <v-card outlined class="ai-history-card mb-4 pa-3">
            <v-row dense align="center">
                <template v-if="preset === 'custom'">
                    <v-col cols="6" sm="3" md="2">
                        <v-text-field v-model="dateFrom" type="date" label="From" dense outlined hide-details @change="reload" />
                    </v-col>
                    <v-col cols="6" sm="3" md="2">
                        <v-text-field v-model="dateTo" type="date" label="To" dense outlined hide-details @change="reload" />
                    </v-col>
                </template>
                <v-col cols="12" sm="6" md="3">
                    <v-text-field
                        v-model="search"
                        label="Search title, page or user"
                        outlined dense hide-details clearable
                        prepend-inner-icon="mdi-magnify"
                        @input="onSearch"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                    <v-autocomplete
                        v-model="userId"
                        :items="userOptions"
                        item-text="label"
                        item-value="id"
                        label="User"
                        outlined dense hide-details clearable
                        prepend-inner-icon="mdi-account-outline"
                        @change="reload"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                    <v-autocomplete
                        v-model="tool"
                        :items="toolOptions"
                        label="Tool used"
                        outlined dense hide-details clearable
                        prepend-inner-icon="mdi-wrench-outline"
                        @change="reload"
                    />
                </v-col>
                <v-col cols="6" sm="3" md="1">
                    <v-select v-model="model" :items="modelOptions" label="Model" outlined dense hide-details clearable @change="reload" />
                </v-col>
                <v-col cols="6" sm="3" md="auto">
                    <v-checkbox v-model="changedOnly" label="Made changes" color="deep-purple" dense hide-details class="mt-0 pt-0" @change="reload" />
                </v-col>
                <v-col v-if="hasFilters" cols="auto">
                    <v-btn text small color="grey darken-1" @click="clearFilters"><v-icon small left>mdi-filter-off-outline</v-icon>Clear</v-btn>
                </v-col>
            </v-row>
        </v-card>

        <!-- Headline numbers -->
        <v-row dense class="mb-2">
            <v-col v-for="t in tiles" :key="t.label" cols="6" sm="4" md>
                <v-card outlined class="ai-history-card ai-history-tile">
                    <div class="ai-history-tile__label">
                        <v-icon small class="mr-1">{{ t.icon }}</v-icon>{{ t.label }}
                    </div>
                    <div class="ai-history-tile__value">
                        <v-progress-circular v-if="statsLoading && !statsLoaded" indeterminate size="18" width="2" color="grey" />
                        <template v-else>{{ t.value }}</template>
                    </div>
                    <div class="ai-history-tile__sub">{{ t.sub }}</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Tool stats -->
        <v-row dense class="mb-2">
            <v-col v-for="chart in toolCharts" :key="chart.title" cols="12" md="6">
                <v-card outlined class="ai-history-card pa-4" style="height: 100%">
                    <div class="d-flex align-baseline mb-3">
                        <div class="subtitle-2 ai-history-ink">{{ chart.title }}</div>
                        <span class="caption grey--text ml-2">{{ chart.unit }}</span>
                    </div>
                    <div v-if="!chart.rows.length" class="caption grey--text py-6 text-center">No tools used in this period</div>
                    <div
                        v-for="row in chart.rows"
                        :key="row.tool"
                        class="ai-history-bar"
                        :class="{ 'ai-history-bar--active': tool === row.tool }"
                        :title="`${row.tool}: ${row.calls} call${row.calls === 1 ? '' : 's'} in ${row.conversations} conversation${row.conversations === 1 ? '' : 's'} · est. ${money(row.cost, 4)} (${money(row.cost_per_call, 4)} per call). Click to filter.`"
                        @click="toggleTool(row.tool)"
                    >
                        <div class="ai-history-bar__name">{{ row.tool }}</div>
                        <div class="ai-history-bar__track">
                            <div class="ai-history-bar__fill" :style="{ width: chart.width(row) }" />
                        </div>
                        <div class="ai-history-bar__value">{{ chart.value(row) }}</div>
                        <div class="ai-history-bar__sub">{{ chart.sub(row) }}</div>
                    </div>
                    <div v-if="chart.note" class="caption grey--text mt-3">{{ chart.note }}</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Conversations / call logs -->
        <v-card outlined class="ai-history-card">
            <v-tabs v-model="tab" height="44" color="deep-purple" class="px-2">
                <v-tab><v-icon small left>mdi-forum-outline</v-icon>Conversations</v-tab>
                <v-tab><v-icon small left>mdi-format-list-bulleted</v-icon>Call logs</v-tab>
            </v-tabs>
            <v-divider />

            <v-data-table
                v-if="tab === 0"
                key="sessions-table"
                :headers="sessionHeaders"
                :items="sessions"
                :loading="sessionsLoading"
                :server-items-length="sessionsTotal"
                :options.sync="sessionOptions"
                :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                class="ai-history-table ai-history-table--rows"
                no-data-text="No conversations match these filters"
                @update:options="loadSessions"
                @click:row="openTranscript"
            >
                <template v-slot:item.title="{ item }">
                    <div class="ai-history-convo">
                        <div class="ai-history-convo__title" :title="item.title">{{ item.title || '(untitled)' }}</div>
                        <div class="ai-history-convo__meta">
                            <span v-if="item.page_label">{{ item.page_label }}</span>
                            <v-chip v-for="c in item.companies || []" :key="c" x-small label color="teal lighten-5" text-color="teal darken-3" class="ml-1" title="Selected company">
                                <v-icon x-small left>mdi-domain</v-icon>{{ c }}
                            </v-chip>
                            <v-chip v-if="item.changed" x-small label color="green lighten-5" text-color="green darken-3" class="ml-1">
                                <v-icon x-small left>mdi-pencil-outline</v-icon>Made changes
                            </v-chip>
                            <v-chip v-if="item.deleted_at" x-small label color="grey lighten-3" text-color="grey darken-3" class="ml-1" title="The user deleted this conversation; it still counts here">
                                <v-icon x-small left>mdi-delete-outline</v-icon>Deleted by user
                            </v-chip>
                        </div>
                    </div>
                </template>
                <template v-slot:item.user_name="{ item }">
                    <user-cell :name="item.user_name" :email="item.user_email" :none="!item.user_id" />
                    <div v-if="item.user_id" class="ai-history-chips mt-1">
                        <v-chip v-if="item.user_role" x-small label color="blue lighten-5" text-color="blue darken-3" title="User role">{{ item.user_role }}</v-chip>
                    </div>
                </template>
                <template v-slot:item.tools="{ item }">
                    <div class="ai-history-chips">
                        <span v-if="!item.tools.length" class="caption grey--text">None</span>
                        <v-chip
                            v-for="t in item.tools"
                            :key="t.name"
                            x-small
                            label
                            :color="tool === t.name ? 'deep-purple' : 'deep-purple lighten-5'"
                            :text-color="tool === t.name ? 'white' : 'deep-purple darken-2'"
                            :title="`${t.calls} call${t.calls === 1 ? '' : 's'}. Click to show only conversations that used it.`"
                            @click.stop="toggleTool(t.name)"
                        >
                            {{ t.name }}<span v-if="t.calls > 1" class="ml-1 ai-history-chip-count">×{{ t.calls }}</span>
                        </v-chip>
                    </div>
                </template>
                <template v-slot:item.model="{ item }">
                    <span :class="item.model === 'advanced' ? 'deep-purple--text' : 'grey--text text--darken-1'">{{ item.model === 'advanced' ? 'Advanced' : 'Standard' }}</span>
                </template>
                <template v-slot:item.cost_usd="{ item }">{{ money(item.cost_usd, 4) }}</template>
                <template v-slot:item.last_message_at="{ item }">
                    <div class="ai-history-date">{{ formatDate(item.last_message_at) }}</div>
                    <div class="caption grey--text">{{ formatTime(item.last_message_at) }}</div>
                </template>
            </v-data-table>

            <template v-else>
                <div class="caption grey--text px-4 pt-3">Every request the bubble made to the AI model (a single message can make several). Follows the date range and user filters.</div>
                <v-data-table
                    key="calls-table"
                    :headers="callHeaders"
                    :items="calls"
                    :loading="callsLoading"
                    :server-items-length="callsTotal"
                    :options.sync="callOptions"
                    :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                    class="ai-history-table"
                    no-data-text="No calls in this period"
                    @update:options="loadCalls"
                >
                    <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}, {{ formatTime(item.created_at) }}</template>
                    <template v-slot:item.user_name="{ item }">
                        <user-cell :name="item.user_name" :email="item.user_email" :none="!item.account_id" />
                    </template>
                    <template v-slot:item.status="{ item }">
                        <span :class="item.status === 'completed' ? 'green--text text--darken-2' : 'red--text text--darken-2'">
                            <v-icon x-small :color="item.status === 'completed' ? 'green darken-2' : 'red darken-2'">{{ item.status === 'completed' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                            {{ item.status }}
                        </span>
                    </template>
                    <template v-slot:item.input_tokens="{ item }">{{ number(item.input_tokens) }}</template>
                    <template v-slot:item.output_tokens="{ item }">{{ number(item.output_tokens) }}</template>
                    <template v-slot:item.cost_usd="{ item }">{{ money(item.cost_usd, 6) }}</template>
                    <template v-slot:item.response_ms="{ item }">{{ (Number(item.response_ms || 0) / 1000).toFixed(1) }}s</template>
                </v-data-table>
            </template>
        </v-card>
        </template>

        <!-- One conversation -->
        <v-dialog v-model="transcriptDialog" max-width="920" scrollable>
            <v-card v-if="transcript" class="ai-history-dialog">
                <v-card-title class="d-block pb-2">
                    <div class="d-flex align-start">
                        <div style="min-width: 0">
                            <div class="ai-history-convo__title text-subtitle-1 font-weight-bold" :title="transcript.title">{{ transcript.title || 'Conversation' }}</div>
                            <div class="caption grey--text">
                                {{ transcript.user_name || 'Unknown user' }}<span v-if="transcript.user_email"> · {{ transcript.user_email }}</span>
                            </div>
                        </div>
                        <v-spacer />
                        <v-btn icon small @click="transcriptDialog = false"><v-icon small>mdi-close</v-icon></v-btn>
                    </div>
                    <div class="ai-history-facts mt-2">
                        <span><v-icon x-small>mdi-clock-outline</v-icon> {{ formatDate(transcript.started_at) }}, {{ formatTime(transcript.started_at) }} – {{ formatTime(transcript.last_message_at) }}</span>
                        <span><v-icon x-small>mdi-message-outline</v-icon> {{ transcript.turns }} message{{ transcript.turns === 1 ? '' : 's' }}</span>
                        <span><v-icon x-small>mdi-chip</v-icon> {{ transcript.model === 'advanced' ? 'Advanced (Sonnet)' : 'Standard (Haiku)' }}</span>
                        <span><v-icon x-small>mdi-currency-usd</v-icon> {{ money(transcript.cost_usd, 4) }}</span>
                    </div>
                    <div v-if="transcriptTools.length" class="ai-history-chips mt-2">
                        <span class="caption grey--text mr-1">Tools used:</span>
                        <v-chip v-for="t in transcriptTools" :key="t.name" x-small label color="deep-purple lighten-5" text-color="deep-purple darken-2">
                            {{ t.name }}<span v-if="t.calls > 1" class="ml-1 ai-history-chip-count">×{{ t.calls }}</span>
                        </v-chip>
                    </div>
                </v-card-title>
                <v-divider />
                <v-card-text class="ai-history-transcript">
                    <v-alert v-if="transcript.deleted_at" dense text type="warning" class="caption mb-3">The user deleted this conversation. It is kept here for the history and stats.</v-alert>
                    <v-alert v-if="transcript.ended_reason" dense text type="info" class="caption mb-3">{{ transcript.ended_reason }}</v-alert>
                    <div v-for="(m, i) in transcript.messages" :key="i" class="ai-history-msg" :class="'ai-history-msg--' + m.role">
                        <template v-if="m.role === 'user'">
                            <div class="ai-history-msg__meta">
                                <v-icon x-small class="mr-1">mdi-account</v-icon><strong>{{ transcript.user_name || 'User' }}</strong>
                                <span class="ml-1">{{ formatTime(m.at) }}</span>
                                <span v-if="m.page" class="ai-history-tag"><v-icon x-small class="mr-1">{{ pageIcon(m.page_kind) }}</v-icon>{{ m.page }}</span>
                                <span v-if="m.company" class="ai-history-tag" title="Selected company"><v-icon x-small class="mr-1">mdi-domain</v-icon>{{ m.company }}</span>
                            </div>
                            <div v-if="m.attachments && m.attachments.length" class="caption mb-1">
                                <v-icon x-small>mdi-paperclip</v-icon> {{ m.attachments.join(', ') }}
                            </div>
                            <div class="ai-history-msg__text">{{ m.text || '(files only)' }}</div>
                        </template>
                        <template v-else-if="m.role === 'note'">
                            <div class="ai-history-msg__meta"><v-icon x-small class="mr-1">mdi-transfer-right</v-icon><strong>Handover from the previous session</strong></div>
                            <div class="ai-history-md" v-html="render(m.text)"></div>
                        </template>
                        <template v-else>
                            <div class="ai-history-msg__meta">
                                <v-icon x-small class="mr-1">mdi-robot-happy-outline</v-icon><strong>Assistant</strong>
                                <span class="ml-1">{{ formatTime(m.at) }}</span>
                                <span v-if="m.model === 'advanced'" class="ai-history-tag">Advanced</span>
                                <span v-if="m.changed" class="ai-history-tag ai-history-tag--changed">Made changes</span>
                                <span v-if="m.cost_usd !== undefined" class="ml-auto">{{ money(m.cost_usd, 4) }}</span>
                            </div>
                            <div v-if="messageTools(m).length" class="ai-history-chips mb-2">
                                <v-chip v-for="t in messageTools(m)" :key="t.name" x-small label outlined color="deep-purple">
                                    <v-icon x-small left>mdi-wrench-outline</v-icon>{{ t.name }}<span v-if="t.calls > 1" class="ml-1 ai-history-chip-count">×{{ t.calls }}</span>
                                </v-chip>
                            </div>
                            <div class="ai-history-md" v-html="render(m.text)"></div>
                        </template>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
// AI Assistant Bubble history (Agency Settings → AI Tools, admins only). Conversations and their
// tools come from /ai_usage/chat_sessions, the headline numbers and tool charts from
// /ai_usage/chat_stats (same filters), and the call logs from /ai_usage/logs for source app_chat.
// All tables are server-side paginated.
// The MCP tab (McpSessionsPanel) shares the date range and has its own filters, stats and table.
import { renderMarkdown } from '@/components/ai-chat/markdown'
import { mapActions } from 'vuex'
import UserCell from './UserCell'
import McpSessionsPanel from './McpSessionsPanel.vue'
import userLocalTime from '@/utils/userLocalTime'

const SOURCE = 'app_chat'
const TOP_TOOLS = 8

export default {
    name: 'AiAssistantHistoryComponent',
    components: { UserCell, McpSessionsPanel },
    mixins: [userLocalTime],
    data() {
        return {
            // 'app' = AI Assistant Bubble, 'mcp' = MCP connector sessions
            source: 'app',
            // 0 = conversations, 1 = call logs
            tab: 0,
            preset: '30',
            dateFrom: '',
            dateTo: '',
            userId: null,
            tool: null,
            model: null,
            changedOnly: false,
            search: '',
            searchTimer: null,
            modelOptions: [
                { text: 'Standard', value: 'standard' },
                { text: 'Advanced', value: 'advanced' },
            ],
            stats: { totals: {}, tools: [], tool_options: [], user_options: [] },
            statsLoading: false,
            statsLoaded: false,
            sessions: [],
            sessionsTotal: 0,
            sessionsLoading: false,
            sessionOptions: { page: 1, itemsPerPage: 25 },
            calls: [],
            callsTotal: 0,
            callsLoading: false,
            callOptions: { page: 1, itemsPerPage: 25 },
            transcript: null,
            transcriptDialog: false,
            mcpLoading: false,
            sessionHeaders: [
                { text: 'Conversation', value: 'title', sortable: false, width: '30%' },
                { text: 'User', value: 'user_name', sortable: false },
                { text: 'Tools used', value: 'tools', sortable: false, width: '28%' },
                { text: 'Messages', value: 'turns', sortable: false, align: 'right' },
                { text: 'Model', value: 'model', sortable: false },
                { text: 'Cost', value: 'cost_usd', sortable: false, align: 'right' },
                { text: 'Last message', value: 'last_message_at', sortable: false, align: 'right' },
            ],
            callHeaders: [
                { text: 'Time', value: 'created_at', sortable: false },
                { text: 'User', value: 'user_name', sortable: false },
                { text: 'Model', value: 'model', sortable: false },
                { text: 'Status', value: 'status', sortable: false },
                { text: 'Input tokens', value: 'input_tokens', sortable: false, align: 'right' },
                { text: 'Output tokens', value: 'output_tokens', sortable: false, align: 'right' },
                { text: 'Cost', value: 'cost_usd', sortable: false, align: 'right' },
                { text: 'Response', value: 'response_ms', sortable: false, align: 'right' },
            ],
        }
    },
    computed: {
        userOptions() {
            return (this.stats.user_options || []).map(u => ({ id: u.id, label: u.email ? `${u.name} (${u.email})` : u.name }))
        },
        toolOptions() {
            // Keep a selected tool in the list even when the other filters hide it
            const options = this.stats.tool_options || []
            return this.tool && !options.includes(this.tool) ? [this.tool, ...options] : options
        },
        hasFilters() {
            return !!(this.userId || this.tool || this.model || this.changedOnly || this.search)
        },
        tiles() {
            const t = this.stats.totals || {}
            const conversations = t.conversations || 0
            return [
                { label: 'Conversations', icon: 'mdi-forum-outline', value: this.number(conversations), sub: `${this.number(t.messages || 0)} messages` },
                { label: 'People', icon: 'mdi-account-multiple-outline', value: this.number(t.users || 0), sub: conversations ? `${(conversations / Math.max(1, t.users)).toFixed(1)} conversations each` : '—' },
                { label: 'Tool calls', icon: 'mdi-wrench-outline', value: this.number(t.tool_calls || 0), sub: conversations ? `${((t.tool_calls || 0) / conversations).toFixed(1)} per conversation` : '—' },
                { label: 'Made changes', icon: 'mdi-pencil-outline', value: this.number(t.changed || 0), sub: conversations ? `${Math.round(((t.changed || 0) / conversations) * 100)}% of conversations` : '—' },
                { label: 'Cost', icon: 'mdi-currency-usd', value: this.money(t.cost || 0, 2), sub: conversations ? `${this.money((t.cost || 0) / conversations, 4)} per conversation` : '—' },
            ]
        },
        toolCharts() {
            const tools = this.stats.tools || []
            const byCalls = tools.slice(0, TOP_TOOLS)
            const byCost = [...tools].sort((a, b) => b.cost - a.cost).slice(0, TOP_TOOLS)
            const maxCalls = Math.max(1, ...byCalls.map(r => r.calls))
            const maxCost = Math.max(0.000001, ...byCost.map(r => r.cost))
            const noTool = (this.stats.totals || {}).no_tool_cost || 0
            return [
                {
                    title: 'Most used tools',
                    unit: 'calls',
                    rows: byCalls,
                    width: r => `${(r.calls / maxCalls) * 100}%`,
                    value: r => this.number(r.calls),
                    sub: r => `${r.conversations} conv.`,
                    note: tools.length > TOP_TOOLS ? `Top ${TOP_TOOLS} of ${tools.length} tools. Click a tool to filter the conversations.` : 'Click a tool to filter the conversations.',
                },
                {
                    title: 'Most expensive tools',
                    unit: 'estimated USD',
                    rows: byCost,
                    width: r => `${(r.cost / maxCost) * 100}%`,
                    value: r => this.money(r.cost, 4),
                    sub: r => `${this.money(r.cost_per_call, 4)}/call`,
                    note: `Each reply's cost is split across the tools it called. Replies that used no tools cost ${this.money(noTool, 4)}.`,
                },
            ]
        },
        transcriptTools() {
            if (!this.transcript) return []
            const counts = {}
            ;(this.transcript.messages || []).filter(m => m.role === 'assistant').forEach(m => {
                this.messageTools(m).forEach(t => { counts[t.name] = (counts[t.name] || 0) + t.calls })
            })
            return Object.keys(counts).map(name => ({ name, calls: counts[name] })).sort((a, b) => b.calls - a.calls)
        },
    },
    watch: {
        // Back on the bubble tab the dates may have changed; its table reloads itself as it mounts
        source(val) {
            if (val === 'app') this.loadStats()
        },
    },
    mounted() {
        this.setPageName('AI Assistant History')
        this.applyPreset()
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer)
    },
    methods: {
        ...mapActions({ setPageName: 'pageNameStore/setPageNameAction' }),
        render(text) {
            return renderMarkdown(text)
        },
        pageIcon(kind) {
            return { lead: 'mdi-account-search-outline', event: 'mdi-calendar-star', supplier: 'mdi-domain' }[kind] || 'mdi-file-outline'
        },
        // Per-call counts are recorded since 2026-10-23; older replies only list each tool once
        messageTools(m) {
            if (m.tool_calls && !Array.isArray(m.tool_calls)) {
                return Object.keys(m.tool_calls).map(name => ({ name, calls: m.tool_calls[name] }))
            }
            return (m.tools || []).map(name => ({ name, calls: 1 }))
        },
        applyPreset() {
            if (this.preset === 'custom') {
                if (!this.dateFrom || !this.dateTo) return
            } else {
                const days = this.preset === 'today' ? 0 : Number(this.preset) - 1
                this.dateTo = this.$date().format('YYYY-MM-DD')
                this.dateFrom = this.$date().subtract(days, 'day').format('YYYY-MM-DD')
            }
            this.refresh()
        },
        // Reloads the open tab with its current filters (the MCP panel loads itself when it opens)
        refresh() {
            if (this.source === 'mcp') {
                // After the new dates reach the panel's props
                this.$nextTick(() => this.$refs.mcp && this.$refs.mcp.reload())
            } else {
                this.reload()
            }
        },
        onSearch() {
            clearTimeout(this.searchTimer)
            this.searchTimer = setTimeout(this.reload, 400)
        },
        toggleTool(name) {
            this.tool = this.tool === name ? null : name
            this.reload()
        },
        clearFilters() {
            this.userId = null
            this.tool = null
            this.model = null
            this.changedOnly = false
            this.search = ''
            this.reload()
        },
        filterParams() {
            const params = { date_from: this.dateFrom, date_to: this.dateTo }
            if (this.userId) params.user_id = this.userId
            if (this.tool) params.tool = this.tool
            if (this.model) params.model = this.model
            if (this.changedOnly) params.changed = 1
            if (this.search) params.search = this.search
            return params
        },
        // Stats follow every filter; both tables go back to page 1. Only one table is mounted at a
        // time, and it loads itself (update:options) when its tab opens or its page changes.
        reload() {
            if (!this.dateFrom || !this.dateTo) return
            this.loadStats()
            if (this.sessionOptions.page !== 1) this.sessionOptions = { ...this.sessionOptions, page: 1 }
            else if (this.tab === 0) this.loadSessions()
            if (this.callOptions.page !== 1) this.callOptions = { ...this.callOptions, page: 1 }
            else if (this.tab === 1) this.loadCalls()
        },
        async loadStats() {
            this.statsLoading = true
            const res = await this.$axios.get('api/ai_usage/chat_stats', this.filterParams())
            this.statsLoading = false
            if (res?.data?.response) {
                this.stats = res.data
                this.statsLoaded = true
            } else {
                this.$toast.error(res?.data?.message || 'Could not load the conversation stats.')
            }
        },
        async loadSessions() {
            if (!this.dateFrom || !this.dateTo) return
            this.sessionsLoading = true
            const res = await this.$axios.get('api/ai_usage/chat_sessions', { ...this.filterParams(), per_page: this.sessionOptions.itemsPerPage, page: this.sessionOptions.page })
            this.sessionsLoading = false
            if (res?.data?.response) {
                this.sessions = res.data.data
                this.sessionsTotal = res.data.total
            } else {
                this.$toast.error(res?.data?.message || 'Could not load the conversations.')
            }
        },
        async loadCalls() {
            if (!this.dateFrom || !this.dateTo) return
            this.callsLoading = true
            const params = { source: SOURCE, date_from: this.dateFrom, date_to: this.dateTo, per_page: this.callOptions.itemsPerPage, page: this.callOptions.page }
            if (this.userId) params.user_id = this.userId
            const res = await this.$axios.get('api/ai_usage/logs', params)
            this.callsLoading = false
            if (res?.data?.response) {
                this.calls = res.data.data
                this.callsTotal = res.data.total
            } else {
                this.$toast.error('Could not load the call logs.')
            }
        },
        async openTranscript(row) {
            const res = await this.$axios.get(`api/ai_usage/chat_sessions/${row.session_key}`)
            if (res?.data?.response) {
                this.transcript = res.data.session
                this.transcriptDialog = true
            } else {
                this.$toast.error(res?.data?.message || 'Could not open the conversation.')
            }
        },
        money(val, digits) {
            return '$' + Number(val || 0).toFixed(digits)
        },
        number(val) {
            return Number(val || 0).toLocaleString()
        },
    },
}
</script>

<style scoped src="./ai-history.css"></style>
